import React from 'react'
import { useCurrentFrame, useVideoConfig, interpolate } from 'remotion'
import { COLORS, FONTS, GLOW, GLASS } from '../shared/theme'
import { fadeIn, entrance, staggerDelay, breathe, pulse, SPRINGS } from '../shared/animations'
import SceneWrapper from '../shared/SceneWrapper'

/**
 * StatGrid — Grid de 2-4 estadísticas con progress rings animados.
 *
 * params: { stats: [{value, label, unit, max, color}], title }
 */

const RING_R = 72
const RING_CX = 92
const RING_CY = 92
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_R

const PALETTE = [
  COLORS.accent.blue,
  COLORS.accent.green,
  COLORS.accent.purple,
  COLORS.accent.orange,
]

function StatCard({ stat, index, frame, fps }) {
  const { value = 0, label = '', unit = '', max = 100, color } = stat
  const accentColor = color || PALETTE[index % PALETTE.length]

  const delay = staggerDelay(index, 12) + 10
  const cardOpacity = fadeIn(frame, delay, 18)
  const cardScale = entrance(frame, fps, delay, SPRINGS.bouncy)
  const ringProgress = entrance(frame, fps, delay + 8, SPRINGS.slow)
  const glowPulse = pulse(frame, 0.05 + index * 0.01, 0.4, 1.0)
  const breath = breathe(frame, 0.022 + index * 0.005, 0.008)

  // Counter: animate from 0 to value
  const counterEnd = delay + 50
  const displayValue = interpolate(frame, [delay + 8, counterEnd], [0, value], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })
  const displayFormatted =
    value % 1 !== 0 ? displayValue.toFixed(1) : Math.round(displayValue).toString()

  // Progress ring
  const fraction = Math.min(Math.abs(value) / (max || 100), 1)
  const filledLength = fraction * ringProgress * RING_CIRCUMFERENCE
  const dashOffset = RING_CIRCUMFERENCE - filledLength

  // Endpoint dot position
  const endAngle = -Math.PI / 2 + fraction * ringProgress * 2 * Math.PI
  const dotX = RING_CX + Math.cos(endAngle) * RING_R
  const dotY = RING_CY + Math.sin(endAngle) * RING_R

  return (
    <div
      style={{
        opacity: cardOpacity,
        transform: `scale(${cardScale * breath})`,
        width: 280,
        borderRadius: 24,
        ...GLASS.card,
        border: `1px solid ${accentColor}25`,
        boxShadow: `${GLOW.md(accentColor)}, 0 20px 40px rgba(0,0,0,0.3)`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '32px 24px 28px',
        gap: 16,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Top accent line */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: '20%',
          width: '60%',
          height: 2,
          background: `linear-gradient(90deg, transparent, ${accentColor}70, transparent)`,
        }}
      />

      {/* Progress ring SVG */}
      <svg width={184} height={184} style={{ overflow: 'visible' }}>
        {/* Track ring */}
        <circle
          cx={RING_CX}
          cy={RING_CY}
          r={RING_R}
          fill="none"
          stroke={`${accentColor}14`}
          strokeWidth={8}
        />
        {/* Glow ring (behind main) */}
        <circle
          cx={RING_CX}
          cy={RING_CY}
          r={RING_R}
          fill="none"
          stroke={accentColor}
          strokeWidth={14}
          strokeDasharray={RING_CIRCUMFERENCE}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          transform={`rotate(-90, ${RING_CX}, ${RING_CY})`}
          opacity={0.15 * glowPulse}
          filter="url(#statBlur)"
          style={{ filter: `blur(8px)` }}
        />
        {/* Main ring */}
        <circle
          cx={RING_CX}
          cy={RING_CY}
          r={RING_R}
          fill="none"
          stroke={accentColor}
          strokeWidth={8}
          strokeDasharray={RING_CIRCUMFERENCE}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          transform={`rotate(-90, ${RING_CX}, ${RING_CY})`}
        />
        {/* Endpoint glow dot */}
        {ringProgress > 0.05 && (
          <>
            <circle cx={dotX} cy={dotY} r={10} fill={accentColor} opacity={0.2 * glowPulse} />
            <circle cx={dotX} cy={dotY} r={5} fill={accentColor} opacity={glowPulse} />
          </>
        )}

        {/* Center: value + unit */}
        <text
          x={RING_CX}
          y={RING_CY - 8}
          textAnchor="middle"
          fontFamily={FONTS.heading}
          fontSize={36}
          fontWeight={800}
          fill={COLORS.text.primary}
        >
          {displayFormatted}
        </text>
        {unit ? (
          <text
            x={RING_CX}
            y={RING_CY + 18}
            textAnchor="middle"
            fontFamily={FONTS.mono}
            fontSize={16}
            fill={accentColor}
            fontWeight={600}
          >
            {unit}
          </text>
        ) : null}
      </svg>

      {/* Label */}
      <div
        style={{
          fontFamily: FONTS.body,
          fontSize: 16,
          fontWeight: 600,
          color: COLORS.text.secondary,
          textAlign: 'center',
          letterSpacing: '0.04em',
          lineHeight: 1.3,
          maxWidth: 200,
        }}
      >
        {label}
      </div>
    </div>
  )
}

export default function StatGrid({ params = {}, artDirection = {} }) {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const { stats = [], title = '' } = params

  const count = Math.min(stats.length, 4)
  const titleOpacity = fadeIn(frame, 0, 18)

  return (
    <SceneWrapper accent={COLORS.accent.blue} texture={artDirection?.texture}>
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 48,
        }}
      >
        {/* Title */}
        {title ? (
          <div
            style={{
              opacity: titleOpacity,
              fontFamily: FONTS.heading,
              fontSize: 28,
              fontWeight: 600,
              color: COLORS.text.muted,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
            }}
          >
            {title}
          </div>
        ) : null}

        {/* Stats grid */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 32,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {stats.slice(0, count).map((stat, i) => (
            <StatCard key={i} stat={stat} index={i} frame={frame} fps={fps} />
          ))}
        </div>
      </div>
    </SceneWrapper>
  )
}
